import {
  EMOJI_ARTWORK_CELL_SIZE,
  EMOJI_ARTWORK_MAX_SEQUENCE_LENGTH,
  EMOJI_ARTWORK_SHEET_SIZE,
  EMOJI_ARTWORK_SHEET_URL,
  EMOJI_ARTWORK_SOURCE_SIZE,
  emojiArtworkKey,
  hasEmojiArtwork,
  resolveEmojiArtworkPosition,
  resolveEmojiRendering,
  resolveRenderableEmojiArtwork,
} from './emojiArtwork.js';
import styles from './EmojiGlyph.module.css';

export { emojiArtworkKey, hasEmojiArtwork, resolveEmojiArtworkPosition };

export const DEFAULT_EMOJI_RENDERING = 'hybrid';

let emojiTextSegmenter;

const EXTENDED_PICTOGRAPHIC_PATTERN = /\p{Extended_Pictographic}/u;
const EMOJI_PRESENTATION_PATTERN = /\p{Emoji_Presentation}/u;
const REGIONAL_INDICATOR_PAIR_PATTERN = /^\p{Regional_Indicator}{2}$/u;
const KEYCAP_SEQUENCE_PATTERN = /^[#*0-9]\uFE0F?\u20E3$/u;
const ZERO_WIDTH_JOINER = 0x200d;
const KEYCAP = 0x20e3;

const codePointOf = (value) => value?.codePointAt(0) ?? -1;
const isRegionalIndicator = (value) => {
  const codePoint = codePointOf(value);
  return codePoint >= 0x1f1e6 && codePoint <= 0x1f1ff;
};
const isKeycapBase = (value) => value === '#' || value === '*' || /^[0-9]$/.test(value);
const isEmojiModifier = (value) => {
  const codePoint = codePointOf(value);
  return codePoint >= 0x1f3fb && codePoint <= 0x1f3ff;
};
const isEmojiVariationSelector = (value) => {
  const codePoint = codePointOf(value);
  return codePoint === 0xfe0e || codePoint === 0xfe0f;
};
const isEmojiTagCharacter = (value) => {
  const codePoint = codePointOf(value);
  return codePoint >= 0xe0020 && codePoint <= 0xe007f;
};
const isLikelyEmojiBase = (value) => {
  const codePoint = codePointOf(value);
  return (
    isEmojiGrapheme(value) ||
    (codePoint >= 0x1f000 && codePoint <= 0x1faff) ||
    (codePoint >= 0x2600 && codePoint <= 0x27ff)
  );
};

const isEmojiGrapheme = (value) =>
  EXTENDED_PICTOGRAPHIC_PATTERN.test(value) ||
  EMOJI_PRESENTATION_PATTERN.test(value) ||
  REGIONAL_INDICATOR_PAIR_PATTERN.test(value) ||
  KEYCAP_SEQUENCE_PATTERN.test(value);

const resolveEmojiTextSegmenter = () => {
  if (emojiTextSegmenter !== undefined) return emojiTextSegmenter;
  try {
    emojiTextSegmenter =
      typeof Intl?.Segmenter === 'function'
        ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
        : null;
  } catch {
    emojiTextSegmenter = null;
  }
  return emojiTextSegmenter;
};

const appendEmojiTextToken = (tokens, type, value) => {
  if (!value) return;
  const previous = tokens.at(-1);
  if (type === 'text' && previous?.type === 'text') {
    previous.value += value;
    return;
  }
  tokens.push({ type, value });
};

const consumeLikelyEmojiSequence = (codePoints, start) => {
  if (isRegionalIndicator(codePoints[start])) {
    return isRegionalIndicator(codePoints[start + 1]) ? start + 2 : start + 1;
  }

  if (isKeycapBase(codePoints[start])) {
    let cursor = start + 1;
    if (isEmojiVariationSelector(codePoints[cursor])) cursor += 1;
    return codePointOf(codePoints[cursor]) === KEYCAP ? cursor + 1 : start;
  }

  if (!isLikelyEmojiBase(codePoints[start])) return start;

  let cursor = start + 1;
  const consumeExtensions = () => {
    while (
      isEmojiVariationSelector(codePoints[cursor]) ||
      isEmojiModifier(codePoints[cursor]) ||
      isEmojiTagCharacter(codePoints[cursor])
    ) {
      cursor += 1;
    }
  };

  consumeExtensions();
  while (
    codePointOf(codePoints[cursor]) === ZERO_WIDTH_JOINER &&
    isLikelyEmojiBase(codePoints[cursor + 1])
  ) {
    cursor += 2;
    consumeExtensions();
  }
  return cursor;
};

const tokenizeEmojiTextWithArtwork = (value) => {
  const codePoints = Array.from(value);
  const tokens = [];
  let index = 0;

  while (index < codePoints.length) {
    const remaining = codePoints.length - index;
    const maximumLength = Math.min(EMOJI_ARTWORK_MAX_SEQUENCE_LENGTH, remaining);
    let emoji = null;

    for (let length = maximumLength; length > 0; length -= 1) {
      const candidate = codePoints.slice(index, index + length).join('');
      if (hasEmojiArtwork(candidate)) {
        emoji = candidate;
        break;
      }
    }

    if (emoji) {
      appendEmojiTextToken(tokens, 'emoji', emoji);
      index += Array.from(emoji).length;
    } else {
      const emojiEnd = consumeLikelyEmojiSequence(codePoints, index);
      if (emojiEnd > index) {
        appendEmojiTextToken(tokens, 'emoji', codePoints.slice(index, emojiEnd).join(''));
        index = emojiEnd;
      } else {
        appendEmojiTextToken(tokens, 'text', codePoints[index]);
        index += 1;
      }
    }
  }

  return tokens;
};

export const tokenizeEmojiText = (text, segmenter = resolveEmojiTextSegmenter()) => {
  const value = String(text ?? '');
  if (!value) return [];
  if (!segmenter) return tokenizeEmojiTextWithArtwork(value);

  const tokens = [];
  Array.from(segmenter.segment(value), ({ segment }) => {
    appendEmojiTextToken(tokens, isEmojiGrapheme(segment) ? 'emoji' : 'text', segment);
    return segment;
  });
  return tokens;
};

const EmojiGlyph = ({
  emoji,
  size = 28,
  className = '',
  title = null,
  rendering = DEFAULT_EMOJI_RENDERING,
  ariaHidden = true,
}) => {
  const normalizedSize = Math.max(12, Number(size) || 28);
  const resolvedRendering = resolveEmojiRendering(emoji, rendering);
  if (resolvedRendering === 'native') {
    return (
      <span
        className={`${styles.nativeFallback} ${className}`.trim()}
        style={{ fontSize: normalizedSize }}
        data-emoji-rendering="native"
        title={title || undefined}
        aria-hidden={ariaHidden || undefined}
      >
        {emoji}
      </span>
    );
  }

  const { position, isFallback } = resolveRenderableEmojiArtwork(emoji);

  const scale = normalizedSize / EMOJI_ARTWORK_SOURCE_SIZE;
  return (
    <span
      className={`${styles.glyph} ${className}`.trim()}
      data-testid="emoji-artwork"
      data-emoji-rendering="artwork"
      data-emoji-fallback={isFallback ? 'true' : undefined}
      style={{
        width: normalizedSize,
        height: normalizedSize,
        backgroundImage: `url(${EMOJI_ARTWORK_SHEET_URL})`,
        backgroundSize: `${EMOJI_ARTWORK_SHEET_SIZE * scale}px ${EMOJI_ARTWORK_SHEET_SIZE * scale}px`,
        backgroundPosition: `${-(position.x * EMOJI_ARTWORK_CELL_SIZE + 1) * scale}px ${-(position.y * EMOJI_ARTWORK_CELL_SIZE + 1) * scale}px`,
      }}
      title={title || undefined}
      aria-hidden={ariaHidden || undefined}
    />
  );
};

export const EmojiText = ({
  text,
  size = 18,
  className = '',
  emojiClassName = '',
  rendering = DEFAULT_EMOJI_RENDERING,
  style,
  ...spanProps
}) => {
  const value = String(text ?? '');
  if (!value) return null;
  const tokens = tokenizeEmojiText(value);
  if (tokens.length === 0) return null;

  return (
    <span className={`${styles.emojiText} ${className}`.trim()} style={style} {...spanProps}>
      {tokens.map((token, index) => {
        if (token.type !== 'emoji') return token.value;
        const resolvedRendering = resolveEmojiRendering(token.value, rendering);
        return (
          <span
            key={`${token.value}-${index}`}
            className={`${styles.emojiToken} ${emojiClassName}`.trim()}
          >
            <EmojiGlyph
              emoji={token.value}
              size={size}
              rendering={resolvedRendering}
              ariaHidden={resolvedRendering !== 'native'}
            />
            {resolvedRendering === 'native' ? null : (
              <span className={styles.accessibleEmoji}>{token.value}</span>
            )}
          </span>
        );
      })}
    </span>
  );
};

export default EmojiGlyph;
